import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Trophy, Users, ThumbsUp, FileText, Plus, Edit, Trash2, 
  Check, X, BarChart3, TrendingUp, Upload, Crown, Handshake,
  UserCheck, UserX, Clock, Search, Mail, Bell, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import TierBadge from '../components/TierBadge';
import StarRating from '../components/StarRating';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = {
  gold: '#D4AF37',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  primary: '#D4AF37',
  secondary: '#8884d8',
  tertiary: '#82ca9d',
  jury: '#E5C048',
  public: '#6B7280'
};

const ROLE_COLORS = {
  admin: 'bg-purple-500/20 text-purple-400',
  jury: 'bg-[#D4AF37]/20 text-[#D4AF37]',
  partner: 'bg-green-500/20 text-green-400',
  public: 'bg-blue-500/20 text-blue-400'
};

const ROLE_ICONS = {
  admin: Crown,
  jury: Crown,
  partner: Handshake,
  public: Users
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [awards, setAwards] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAward, setEditingAward] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('approved');
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Debounced user search / filter
    const t = setTimeout(() => { fetchUsers(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, userRoleFilter, userStatusFilter]);

  const fetchData = async () => {
    try {
      const [statsRes, analyticsRes, awardsRes, nominationsRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/analytics`, { withCredentials: true }),
        axios.get(`${API_URL}/api/awards`),
        axios.get(`${API_URL}/api/nominations?status=pending`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users/pending`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setAwards(awardsRes.data);
      setNominations(nominationsRes.data);
      setPendingUsers(pendingRes.data);
      await fetchUsers();
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = {};
      if (userSearch.trim()) params.search = userSearch.trim();
      if (userRoleFilter && userRoleFilter !== 'all') params.role = userRoleFilter;
      if (userStatusFilter && userStatusFilter !== 'all') params.status = userStatusFilter;
      const res = await axios.get(`${API_URL}/api/admin/users`, { params, withCredentials: true });
      setUsers(res.data);
    } catch (error) {
      // silently; initial fetchData shows toast
    }
  };

  const fetchNotifications = async (eventType = notificationFilter) => {
    setNotificationsLoading(true);
    try {
      const params = {};
      if (eventType && eventType !== 'all') params.event_type = eventType;
      const res = await axios.get(`${API_URL}/api/admin/notifications`, { params, withCredentials: true });
      setNotifications(res.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleApproveUser = async (userId, approved, assignedRole = null) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/approve`, {
        user_id: userId,
        approved,
        assigned_role: assignedRole
      }, { withCredentials: true });
      toast.success(approved ? 'User approved' : 'User rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/role`, {
        user_id: userId,
        role: newRole
      }, { withCredentials: true });
      toast.success(`Role changed to ${newRole}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to change role');
    }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.name}" (${u.email})?\nThis will also remove their votes, nominations and partner profile. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${u.id}`, { withCredentials: true });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSaveEditUser = async (userId, payload) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}`, payload, { withCredentials: true });
      toast.success('User updated');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleApproveNomination = async (id) => {
    try {
      await axios.put(`${API_URL}/api/nominations/${id}/approve`, {}, { withCredentials: true });
      toast.success('Nomination approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve nomination');
    }
  };

  const handleRejectNomination = async (id) => {
    try {
      await axios.put(`${API_URL}/api/nominations/${id}/reject`, {}, { withCredentials: true });
      toast.success('Nomination rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject nomination');
    }
  };

  const handleDeleteAward = async (id) => {
    if (!window.confirm('Are you sure you want to delete this award?')) return;
    try {
      await axios.delete(`${API_URL}/api/awards/${id}`, { withCredentials: true });
      toast.success('Award deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete award');
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </main>
    );
  }

  const categoryPieData = Object.entries(stats?.category_stats || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  const tierPieData = Object.entries(stats?.tier_stats || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
    color: CHART_COLORS[name] || CHART_COLORS.primary
  }));

  return (
    <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-2">
            Admin Panel
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white">
            Dashboard
          </h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={<Trophy className="w-5 h-5" />} label="Awards" value={stats?.total_awards || 0} color="gold" />
          <StatCard icon={<Users className="w-5 h-5" />} label="Users" value={stats?.approved_users || 0} color="silver" />
          <StatCard icon={<Clock className="w-5 h-5" />} label="Pending Users" value={stats?.pending_users || 0} color="white" />
          <StatCard icon={<ThumbsUp className="w-5 h-5" />} label="Total Votes" value={stats?.total_votes || 0} color="bronze" />
          <StatCard icon={<Crown className="w-5 h-5" />} label="Jury Votes" value={stats?.total_jury_votes || 0} color="gold" />
          <StatCard icon={<FileText className="w-5 h-5" />} label="Nominations" value={stats?.pending_nominations || 0} color="silver" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-[#121212] border border-white/10 flex-wrap">
            <TabsTrigger value="pending" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">
              Pending Approvals ({pendingUsers.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">
              All Users
            </TabsTrigger>
            <TabsTrigger value="awards" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">
              Awards
            </TabsTrigger>
            <TabsTrigger value="nominations" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">
              Nominations ({nominations.length})
            </TabsTrigger>
            <TabsTrigger
              value="notifications"
              onClick={() => fetchNotifications()}
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]"
              data-testid="tab-notifications"
            >
              Notifications
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A]">
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Pending Approvals Tab */}
          <TabsContent value="pending">
            <div className="dashboard-card p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-[#D4AF37]" />
                Pending User Approvals
              </h2>
              
              {pendingUsers.length === 0 ? (
                <div className="text-center py-12">
                  <UserCheck className="w-12 h-12 mx-auto text-white/20 mb-4" />
                  <p className="text-white/50">No pending approvals</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((u) => {
                    const RoleIcon = ROLE_ICONS[u.requested_role] || Users;
                    return (
                      <div
                        key={u.id}
                        className="p-4 rounded-xl bg-[#0A0A0A] border border-white/5"
                        data-testid={`pending-user-${u.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960F] flex items-center justify-center">
                              <span className="text-lg font-semibold text-[#0A0A0A]">
                                {u.name?.charAt(0).toUpperCase() || 'U'}
                              </span>
                            </div>
                            <div>
                              <p className="text-white font-medium">{u.name}</p>
                              <p className="text-white/50 text-sm">{u.email}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-white/40 text-xs">Requested:</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${ROLE_COLORS[u.requested_role] || ROLE_COLORS.public}`}>
                                  <RoleIcon className="w-3 h-3" />
                                  {u.requested_role || 'public'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Select onValueChange={(role) => handleApproveUser(u.id, true, role)}>
                              <SelectTrigger className="w-[140px] bg-green-600 hover:bg-green-700 text-white border-0">
                                <SelectValue placeholder="Approve as..." />
                              </SelectTrigger>
                              <SelectContent className="bg-[#121212] border-white/10">
                                <SelectItem value="public">Public Member</SelectItem>
                                <SelectItem value="jury">Jury Member</SelectItem>
                                <SelectItem value="partner">Partner</SelectItem>
                                <SelectItem value="professional_partner">Professional Partner</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              onClick={() => handleApproveUser(u.id, false)}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid={`reject-user-${u.id}`}
                            >
                              <UserX className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                        <p className="text-white/30 text-xs mt-3">
                          Registered: {new Date(u.created_at).toLocaleString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="users">
            <div className="dashboard-card p-6">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h2 className="text-xl font-semibold text-white">User Management</h2>
                <div className="flex gap-4 flex-wrap">
                  {Object.entries(stats?.role_stats || {}).map(([role, count]) => {
                    const RoleIcon = ROLE_ICONS[role] || Users;
                    return (
                      <div key={role} className="flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] rounded-lg">
                        <RoleIcon className={`w-4 h-4 ${role === 'jury' || role === 'admin' ? 'text-[#D4AF37]' : role === 'partner' ? 'text-green-400' : 'text-blue-400'}`} />
                        <span className="text-white/70 text-sm capitalize">{role}:</span>
                        <span className="text-white font-semibold">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Search + Filters */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
                    data-testid="user-search-input"
                  />
                </div>
                <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                  <SelectTrigger className="w-full sm:w-[160px] bg-[#0A0A0A] border-white/10 text-white" data-testid="user-role-filter">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="jury">Jury</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="professional_partner">Professional Partner</SelectItem>
                    <SelectItem value="public">Public</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[160px] bg-[#0A0A0A] border-white/10 text-white" data-testid="user-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-white/40 text-xs mb-3" data-testid="user-count">
                Showing {users.length} {users.length === 1 ? 'user' : 'users'}
              </p>

              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-white/40">No users match the filters.</td>
                      </tr>
                    ) : users.map((u) => {
                      const RoleIcon = ROLE_ICONS[u.role] || Users;
                      const statusColor = u.status === 'approved' ? 'text-green-400'
                        : u.status === 'pending' ? 'text-yellow-400'
                        : 'text-red-400';
                      return (
                        <tr key={u.id} data-testid={`user-row-${u.id}`}>
                          <td>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960F] flex items-center justify-center">
                                <span className="text-sm font-semibold text-[#0A0A0A]">
                                  {u.name?.charAt(0).toUpperCase() || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="text-white font-medium">{u.name}</p>
                                <p className="text-white/50 text-sm">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 w-fit ${ROLE_COLORS[u.role] || ROLE_COLORS.public}`}>
                              <RoleIcon className="w-3 h-3" />
                              {u.role}
                            </span>
                          </td>
                          <td>
                            <span className={`${statusColor} text-sm capitalize`}>{u.status}</span>
                          </td>
                          <td>
                            <span className="text-white/50 text-sm">
                              {new Date(u.created_at).toLocaleDateString()}
                            </span>
                          </td>
                          <td>
                            <div className="flex items-center gap-1">
                              {u.role !== 'admin' && u.status === 'approved' && (
                                <Select value={u.role} onValueChange={(role) => handleChangeRole(u.id, role)}>
                                  <SelectTrigger className="w-[110px] bg-[#0A0A0A] border-white/10 text-white h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#121212] border-white/10">
                                    <SelectItem value="public">Public</SelectItem>
                                    <SelectItem value="jury">Jury</SelectItem>
                                    <SelectItem value="partner">Partner</SelectItem>
                                    <SelectItem value="professional_partner">Professional Partner</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingUser(u)}
                                className="h-8 w-8 p-0 text-white/70 hover:text-[#D4AF37]"
                                data-testid={`edit-user-${u.id}`}
                                title="Edit user"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {u.id !== user?.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteUser(u)}
                                  className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  data-testid={`delete-user-${u.id}`}
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Awards Tab */}
          <TabsContent value="awards">
            <div className="dashboard-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Manage Awards</h2>
                <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]" data-testid="add-award-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Award
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-white font-['Playfair_Display']">
                        {editingAward ? 'Edit Award' : 'Add New Award'}
                      </DialogTitle>
                    </DialogHeader>
                    <AwardForm
                      award={editingAward}
                      onSuccess={() => {
                        setShowAddModal(false);
                        setEditingAward(null);
                        fetchData();
                      }}
                      onCancel={() => {
                        setShowAddModal(false);
                        setEditingAward(null);
                      }}
                    />
                  </DialogContent>
                </Dialog>
              </div>

              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Tier</th>
                      <th>Stars</th>
                      <th>Votes</th>
                      <th>Weighted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {awards.map((award) => (
                      <tr key={award.id}>
                        <td>
                          <div className="flex items-center gap-3">
                            {award.image_url && (
                              <img src={award.image_url} alt={award.name} className="w-10 h-10 rounded-lg object-cover" />
                            )}
                            <div>
                              <p className="text-white font-medium">{award.name}</p>
                              <p className="text-white/50 text-sm">{award.location}, {award.country}</p>
                            </div>
                          </div>
                        </td>
                        <td><span className="capitalize text-white/70">{award.category}</span></td>
                        <td><TierBadge tier={award.tier} size="sm" /></td>
                        <td><StarRating rating={award.star_rating} size="sm" /></td>
                        <td><span className="text-white/70">{award.vote_count}</span></td>
                        <td><span className="text-[#D4AF37] font-semibold">{award.total_weighted_votes || award.vote_count}</span></td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingAward(award); setShowAddModal(true); }}
                              className="text-white/70 hover:text-white"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAward(award.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Nominations Tab */}
          <TabsContent value="nominations">
            <div className="dashboard-card p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Pending Nominations</h2>
              
              {nominations.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-white/20 mb-4" />
                  <p className="text-white/50">No pending nominations</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {nominations.map((nomination) => (
                    <div key={nomination.id} className="p-4 rounded-xl bg-[#0A0A0A] border border-white/5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{nomination.name}</h3>
                          <p className="text-white/50 text-sm mb-2">
                            {nomination.location}, {nomination.country} • {nomination.category}
                          </p>
                          <p className="text-white/70 text-sm mb-3">{nomination.description}</p>
                          <div className="bg-[#1A1A1A] p-3 rounded-lg">
                            <p className="text-sm text-white/50 mb-1">Reason:</p>
                            <p className="text-white/80 text-sm">{nomination.reason}</p>
                          </div>
                          <p className="text-white/40 text-xs mt-3">By {nomination.submitted_by_name}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleApproveNomination(nomination.id)} className="bg-green-600 hover:bg-green-700 text-white">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" onClick={() => handleRejectNomination(nomination.id)} className="bg-red-600 hover:bg-red-700 text-white">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <div className="dashboard-card p-6">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#D4AF37]" />
                  Notification Log
                  <span className="px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400 text-xs font-medium">MOCKED</span>
                </h2>
                <div className="flex gap-2">
                  <Select value={notificationFilter} onValueChange={(v) => { setNotificationFilter(v); fetchNotifications(v); }}>
                    <SelectTrigger className="w-[200px] bg-[#0A0A0A] border-white/10 text-white" data-testid="notifications-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      <SelectItem value="all">All Events</SelectItem>
                      <SelectItem value="registration_pending">Registration Pending</SelectItem>
                      <SelectItem value="registration_approved">Registration Approved</SelectItem>
                      <SelectItem value="registration_rejected">Registration Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={() => fetchNotifications()} className="border-white/10 text-white" data-testid="refresh-notifications">
                    <RefreshCw className={`w-4 h-4 ${notificationsLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              <p className="text-white/40 text-xs mb-4">
                Email sending is mocked. Entries below represent emails that would have been delivered to users.
              </p>

              {notificationsLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => <div key={i} className="h-24 skeleton rounded-xl" />)}
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 mx-auto text-white/20 mb-4" />
                  <p className="text-white/50">No notifications logged yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((n) => {
                    const iconColor = n.event_type === 'registration_approved' ? 'text-green-400'
                      : n.event_type === 'registration_rejected' ? 'text-red-400'
                      : 'text-[#D4AF37]';
                    return (
                      <div
                        key={n.id}
                        className="p-4 rounded-xl bg-[#0A0A0A] border border-white/5"
                        data-testid={`notification-${n.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <Mail className={`w-5 h-5 mt-1 ${iconColor}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-medium">{n.subject}</span>
                              <span className="px-2 py-0.5 rounded bg-white/5 text-white/60 text-xs">{n.event_type}</span>
                            </div>
                            <p className="text-white/50 text-sm mt-1">To: <span className="text-white/70">{n.recipient}</span></p>
                            <pre className="whitespace-pre-wrap text-white/60 text-xs mt-2 font-['Outfit']">{n.body}</pre>
                            <p className="text-white/30 text-xs mt-2">
                              {new Date(n.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="dashboard-card p-6 lg:col-span-2">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                  Votes Over Time (Last 30 Days)
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics?.votes_over_time || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                      <YAxis stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #333', borderRadius: '8px' }} />
                      <Legend />
                      <Line type="monotone" dataKey="total" stroke={CHART_COLORS.primary} strokeWidth={2} name="Total" dot={false} />
                      <Line type="monotone" dataKey="jury" stroke={CHART_COLORS.jury} strokeWidth={2} name="Jury" dot={false} />
                      <Line type="monotone" dataKey="public" stroke={CHART_COLORS.public} strokeWidth={2} name="Public" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="dashboard-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  User Registrations
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics?.users_over_time || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#666" tick={{ fill: '#999', fontSize: 10 }} />
                      <YAxis stroke="#666" tick={{ fill: '#999', fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #333', borderRadius: '8px' }} />
                      <Bar dataKey="count" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} name="Registrations" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="dashboard-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
                  Awards by Category
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={categoryPieData} cx="50%" cy="50%" outerRadius={80} fill={CHART_COLORS.primary} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {categoryPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={Object.values(CHART_COLORS)[index % 3]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #333', borderRadius: '8px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="dashboard-card p-6 lg:col-span-2">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                  Top Awards Leaderboard
                </h3>
                <div className="space-y-3">
                  {(analytics?.leaderboard || []).slice(0, 5).map((award, index) => (
                    <div key={award.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#0A0A0A]">
                      <span className={`text-lg font-bold w-6 ${index === 0 ? 'text-[#D4AF37]' : index === 1 ? 'text-[#C0C0C0]' : index === 2 ? 'text-[#CD7F32]' : 'text-white/50'}`}>
                        #{index + 1}
                      </span>
                      {award.image_url && <img src={award.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{award.name}</p>
                        <p className="text-white/50 text-xs capitalize">{award.category}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#D4AF37] font-semibold">{award.total_weighted_votes || award.vote_count}</p>
                        <p className="text-white/40 text-xs">weighted</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit User Dialog */}
        <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
          <DialogContent className="bg-[#121212] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-['Playfair_Display']">Edit User</DialogTitle>
            </DialogHeader>
            {editingUser && (
              <EditUserForm
                user={editingUser}
                currentAdminId={user?.id}
                onSave={handleSaveEditUser}
                onCancel={() => setEditingUser(null)}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}

function EditUserForm({ user, currentAdminId, onSave, onCancel }) {
  const [name, setName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [role, setRole] = useState(user.role || 'public');
  const [status, setStatus] = useState(user.status || 'approved');
  const [saving, setSaving] = useState(false);

  const isSelf = user.id === currentAdminId;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    if (name.trim() && name !== user.name) payload.name = name.trim();
    if (email.trim() && email !== user.email) payload.email = email.trim().toLowerCase();
    if (role !== user.role) payload.role = role;
    if (status !== user.status) payload.status = status;
    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save');
      setSaving(false);
      return;
    }
    await onSave(user.id, payload);
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="edit-user-form">
      <div>
        <Label className="text-white/70">Full Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} required className="bg-[#0A0A0A] border-white/10 text-white" data-testid="edit-user-name" />
      </div>
      <div>
        <Label className="text-white/70">Email</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-[#0A0A0A] border-white/10 text-white" data-testid="edit-user-email" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-white/70">Role</Label>
          <Select value={role} onValueChange={setRole} disabled={isSelf}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="public">Public</SelectItem>
              <SelectItem value="jury">Jury</SelectItem>
              <SelectItem value="partner">Partner</SelectItem>
              <SelectItem value="professional_partner">Professional Partner</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/70">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="edit-user-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {isSelf && <p className="text-yellow-400/80 text-xs">You cannot change your own role.</p>}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="border-white/10 text-white">Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]" data-testid="edit-user-save">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}

function StatCard({ icon, label, value, color }) {
  const colorClasses = {
    gold: 'text-[#D4AF37] bg-[#D4AF37]/10',
    silver: 'text-[#C0C0C0] bg-[#C0C0C0]/10',
    bronze: 'text-[#CD7F32] bg-[#CD7F32]/10',
    white: 'text-white bg-white/10'
  };

  return (
    <div className="dashboard-card p-4">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-['Playfair_Display'] font-semibold text-white mb-0.5">{value}</p>
      <p className="text-white/50 text-xs">{label}</p>
    </div>
  );
}

function AwardForm({ award, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    name: award?.name || '',
    category: award?.category || 'hotel',
    description: award?.description || '',
    location: award?.location || '',
    country: award?.country || '',
    star_rating: award?.star_rating || 5,
    tier: award?.tier || 'gold',
    image_url: award?.image_url || '',
    features: award?.features?.join(', ') || '',
    year: award?.year || new Date().getFullYear()
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image_url: response.data.url });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...formData, features: formData.features.split(',').map(f => f.trim()).filter(Boolean) };
      if (award) {
        await axios.put(`${API_URL}/api/awards/${award.id}`, payload, { withCredentials: true });
        toast.success('Award updated');
      } else {
        await axios.post(`${API_URL}/api/awards`, payload, { withCredentials: true });
        toast.success('Award created');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-white/70">Name</Label>
          <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="bg-[#0A0A0A] border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white/70">Category</Label>
          <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="hotel">Hotel</SelectItem>
              <SelectItem value="resort">Resort</SelectItem>
              <SelectItem value="destination">Destination</SelectItem>
              <SelectItem value="air">Air</SelectItem>
              <SelectItem value="experience">Experience</SelectItem>
              <SelectItem value="b2b">B2B</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/70">Tier</Label>
          <Select value={formData.tier} onValueChange={(value) => setFormData({ ...formData, tier: value })}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="gold">Gold</SelectItem>
              <SelectItem value="silver">Silver</SelectItem>
              <SelectItem value="bronze">Bronze</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/70">Location</Label>
          <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required className="bg-[#0A0A0A] border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white/70">Country</Label>
          <Input value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} required className="bg-[#0A0A0A] border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white/70">Star Rating</Label>
          <Select value={formData.star_rating.toString()} onValueChange={(value) => setFormData({ ...formData, star_rating: parseInt(value) })}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              {[5, 4, 3, 2, 1].map((star) => (<SelectItem key={star} value={star.toString()}>{star} Stars</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/70">Year</Label>
          <Input type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })} required className="bg-[#0A0A0A] border-white/10 text-white" />
        </div>
        <div className="col-span-2">
          <Label className="text-white/70">Image</Label>
          <div className="flex gap-2">
            <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="URL or upload" className="bg-[#0A0A0A] border-white/10 text-white flex-1" />
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <Button type="button" variant="outline" className="border-white/10 text-white" disabled={uploading}>
                <Upload className="w-4 h-4 mr-2" />{uploading ? '...' : 'Upload'}
              </Button>
            </label>
          </div>
          {formData.image_url && <img src={formData.image_url} alt="Preview" className="mt-2 h-20 w-32 object-cover rounded-lg" />}
        </div>
        <div className="col-span-2">
          <Label className="text-white/70">Description</Label>
          <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={3} className="bg-[#0A0A0A] border-white/10 text-white resize-none" />
        </div>
        <div className="col-span-2">
          <Label className="text-white/70">Features (comma-separated)</Label>
          <Input value={formData.features} onChange={(e) => setFormData({ ...formData, features: e.target.value })} placeholder="Spa, Restaurant, Pool" className="bg-[#0A0A0A] border-white/10 text-white" />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="border-white/10 text-white">Cancel</Button>
        <Button type="submit" disabled={loading} className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A]">
          {loading ? 'Saving...' : award ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
